import * as yup from 'yup';
import { startOfDay } from 'date-fns';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddInvitationMutation as AddInvitation, GetInvitationsQuery as GetInvitations } from '__generated__/graphql';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddInvitationMutation from './AddInvitationMutation';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Event = GetInvitations['project']['event'];
type AddedInvitation = AddInvitation['addInvitation'];

const addInvitationFormSchema = yup.object().shape({
  prefix: yup.string().required(),
});

export interface AddInvitationFormProps {
  event: Event;
  onSuccess?: (invitation: AddedInvitation) => void;
}

const AddInvitationForm = ({
  event, onSuccess,
}: AddInvitationFormProps) => {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      event: { id: event.id },
      prefix: '',
      start_date: formatDate(
        startOfDay(new Date()),
        { format: 'internal_date_time', timezone: 'UTC' },
      ),
      end_date: null,
      active: !event.active,
    },
  });

  const [addInvitation, { error, loading }] = useMutation(AddInvitationMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ addInvitation }) => {
      notifier.success(t('notifications.invitation_added'));
      onSuccess?.(addInvitation);
    },
    onError: () => {
      //
    },
  });

  const { valid, errors } = useValidation({
    schema: addInvitationFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addInvitation()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_an_invitation_code')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_invitation_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup
          sc={{ valid: !errors.title, touched: form.touched.title }}
        >
          <UI.InputLabel htmlFor="InvitationPrefix">
            {t('invitation_code')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            id="InvitationPrefix"
            {...form.text('prefix')}
            placeholder={t('invitation_code_placeholder')}
            autoFocus
          />
          <UI.ErrorMessages attribute={t('invitation_code')} errors={errors.prefix} />
        </UI.InputGroup>
      </UI.FormGrid>

      <UI.HR />

      <UI.FormButtons
        form={form}
        submitLabel={t('add_invitation_code')}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default AddInvitationForm;
